/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../theme/ThemeContext";

/**
 * UIRulesAssistant (Chatbot Version)
 * 
 * A conversational interface for configuring table UI rules.
 * 
 * ==========================================
 * JSON Structure of UI Rules:
 * ==========================================
 * {
 *   "insert_button_label": "Create Record",      // Label for the submit button when creating
 *   "update_button_label": "Update Record",      // Label for the submit button when updating
 *   "insert_form_title": "New Record",           // Title of the form when creating
 *   "update_form_title": "Edit Record",          // Title of the form when updating
 *   "insert_success_message": "Created!",        // Custom message on successful creation
 *   "update_success_message": "Updated!",        // Custom message on successful update
 *   "generic_error_message": "Error occurred",   // Custom generic error message
 *   "on_insert_success": {                       // Action to take after insert
 *     "action": "show" | "redirect",             // 'show' = toast/stay, 'redirect' = navigate
 *     "identifier": "list" | "record" | "form"   // Target view
 *   },
 *   "on_update_success": {                       // Action to take after update
 *     "action": "show" | "redirect",
 *     "identifier": "list" | "record"
 *   }
 * }
 * ==========================================
 */
export default function UIRulesAssistant({ open, onClose, initialRules, onSave }) {
    const { theme } = useTheme();
    const scrollRef = useRef(null);

    // Core Rules State
    const [rules, setRules] = useState({
        insert_button_label: "",
        update_button_label: "",
        insert_form_title: "",
        update_form_title: "",
        insert_success_message: "",
        update_success_message: "",
        generic_error_message: "",
        on_insert_success_action: "show",
        on_insert_success_identifier: "list",
        on_update_success_action: "show",
        on_update_success_identifier: "list"
    });

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [inputContext, setInputContext] = useState(null); // { key: string, label: string }

    // Init Logic
    useEffect(() => {
        if (open) {
            if (messages.length === 0) {
                addBotMessage("Greetings. I am your UI Rules Assistant. I shall guide you in shaping the behavior of your tables.\n\nTogether, we can define how users interact with your forms. How shall we begin our journey?", [
                    { label: "Begin Configuration", value: "show_main_menu" }
                ]);
            } else {
                // Resume flow
                addBotMessage("Welcome back. Shall we resume our session?", [
                    { label: "Yes, Resume", value: "resume_session" },
                    { label: "Start Over", value: "clear_rules_init" }
                ]);
            }
            if (initialRules) {
                setRules(prev => ({
                    ...prev,

                    insert_button_label: initialRules.insert_button_label || "",
                    update_button_label: initialRules.update_button_label || "",
                    insert_form_title: initialRules.insert_form_title || "",
                    update_form_title: initialRules.update_form_title || "",
                    insert_success_message: initialRules.insert_success_message || "",
                    update_success_message: initialRules.update_success_message || "",
                    generic_error_message: initialRules.generic_error_message || "",

                    on_insert_success_action:
                        initialRules.on_insert_success?.action || "show",
                    on_insert_success_identifier:
                        initialRules.on_insert_success?.identifier || "list",

                    on_update_success_action:
                        initialRules.on_update_success?.action || "show",
                    on_update_success_identifier:
                        initialRules.on_update_success?.identifier || "list",
                }));
            }
        }
    }, [open]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const addBotMessage = (text, options = null) => {
        setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text, options }]);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text }]);
    };

    const handleOptionSelect = (option) => {
        if (!option) return;
        addUserMessage(option.label);
        processAction(option.value, option);
    };

    /**
     * Handles text input submissions.
     * Updates the specific rule key based on inputContext.
     */
    const handleTextSubmit = () => {
        if (!inputValue.trim()) return;

        // Validation Logic
        if (inputContext) {
            const val = inputValue.trim();
            let error = "";

            if (inputContext.key.includes("button_label")) {
                if (val.length > 32) error = "Button labels cannot exceed 32 characters.";
            } else if (inputContext.key.includes("_title") || inputContext.key.includes("_message")) {
                if (val.length > 512) error = "This text cannot exceed 512 characters.";
            }

            if (error) {
                addBotMessage(`A good design requires brevity. ${error}\nPlease try again.`);
                addUserMessage(inputValue); // Show user input so they see what they typed
                // Clear input but keep context
                setInputValue("");
                return;
            }

            addUserMessage(inputValue);
            const newRules = { ...rules, [inputContext.key]: val };
            setRules(newRules);

            addBotMessage(`It is done. ${inputContext.label} has been set to "${val}".`);
            setInputContext(null); // Clear context

            // Better UX: Return to the category they were in. 
            // We can infer category from key or just go to main menu. Main menu is safest.
            setTimeout(() => showMainMenu("What is our next step?"), 600);
        } else {
            addUserMessage(inputValue);
            addBotMessage("Please select an option from the choices above.");
            showMainMenu();
        }
        setInputValue("");
    };

    // --- Menus ---

    const showMainMenu = (text = "Please select a category:") => {
        addBotMessage(text, [
            { label: "Labels & Messages", value: "menu_cats_labels" },
            { label: "Post-Action Behaviors", value: "menu_cats_actions" },
            { label: "Review Rules", value: "review" },
            { label: "Clear All Rules", value: "clear_rules_init" },
            { label: "Save & Depart", value: "save" }
        ]);
    };

    const showLabelsMenu = () => {
        addBotMessage("Let us refine the language of your interface. What shall we adjust?", [
            { label: "Insert Button Label", value: "edit_insert_btn" },
            { label: "Update Button Label", value: "edit_update_btn" },
            { label: "Insert Form Title", value: "edit_insert_title" },
            { label: "Update Form Title", value: "edit_update_title" },
            { label: "Insert Success Msg", value: "edit_insert_msg" },
            { label: "Update Success Msg", value: "edit_update_msg" },
            { label: "Generic Error Msg", value: "edit_error_msg" },
            { label: "« Return to Main Menu", value: "show_main_menu" }
        ]);
    };

    const showActionsMenu = () => {
        addBotMessage("How should the application respond when a record is created or updated?", [
            { label: "On Insert Success", value: "menu_insert_success" },
            { label: "On Update Success", value: "menu_update_success" },
            { label: "« Return to Main Menu", value: "show_main_menu" }
        ]);
    };

    // --- Action Processor ---

    const processAction = (action, optionObj) => {
        switch (action) {
            case "show_main_menu":
                showMainMenu();
                break;

            case "resume_session":
                showMainMenu("Splendid. How shall we proceed?");
                break;

            // Categories
            case "menu_cats_labels":
                showLabelsMenu();
                break;
            case "menu_cats_actions":
                showActionsMenu();
                break;

            // --- Label/Text Inputs ---
            case "edit_insert_btn":
                setInputContext({ key: "insert_button_label", label: "Insert Button Label" });
                addBotMessage(`What shall we name the Insert Button? (Max 32 chars)\n(Current: ${rules.insert_button_label || "Default"})`);
                break;
            case "edit_update_btn":
                setInputContext({ key: "update_button_label", label: "Update Button Label" });
                addBotMessage(`What shall we name the Update Button? (Max 32 chars)\n(Current: ${rules.update_button_label || "Default"})`);
                break;
            case "edit_insert_title":
                setInputContext({ key: "insert_form_title", label: "Insert Form Title" });
                addBotMessage(`Provide a title for the Creation Form (Max 512 chars).\n(Current: ${rules.insert_form_title || "Default"})`);
                break;
            case "edit_update_title":
                setInputContext({ key: "update_form_title", label: "Update Form Title" });
                addBotMessage(`Provide a title for the Editing Form (Max 512 chars).\n(Current: ${rules.update_form_title || "Default"})`);
                break;
            case "edit_insert_msg":
                setInputContext({ key: "insert_success_message", label: "Insert Success Message" });
                addBotMessage(`What message should be shown upon successful creation? (Max 512 chars)\n(Current: ${rules.insert_success_message || "Default"})`);
                break;
            case "edit_update_msg":
                setInputContext({ key: "update_success_message", label: "Update Success Message" });
                addBotMessage(`What message should be shown upon successful update? (Max 512 chars)\n(Current: ${rules.update_success_message || "Default"})`);
                break;
            case "edit_error_msg":
                setInputContext({ key: "generic_error_message", label: "Generic Error Message" });
                addBotMessage(`How should we describe an error to the user? (Max 512 chars)\n(Current: ${rules.generic_error_message || "Default"})`);
                break;

            // --- Actions Logic (Nested Drilldown) ---
            case "menu_insert_success":
                addBotMessage("Action after Insert:", [
                    { label: "Show Message (Default)", value: "set_insert_action_show" },
                    { label: "Redirect", value: "set_insert_action_redirect" }
                ]);
                break;
            case "set_insert_action_show":
                setRules(prev => ({ ...prev, on_insert_success_action: "show" }));
                askTarget("on_insert_success_identifier", "Insert");
                break;
            case "set_insert_action_redirect":
                setRules(prev => ({ ...prev, on_insert_success_action: "redirect" }));
                askTarget("on_insert_success_identifier", "Insert");
                break;

            case "menu_update_success":
                addBotMessage("Action after Update:", [
                    { label: "Show Message (Default)", value: "set_update_action_show" },
                    { label: "Redirect", value: "set_update_action_redirect" }
                ]);
                break;
            case "set_update_action_show":
                setRules(prev => ({ ...prev, on_update_success_action: "show" }));
                askTarget("on_update_success_identifier", "Update");
                break;
            case "set_update_action_redirect":
                setRules(prev => ({ ...prev, on_update_success_action: "redirect" }));
                askTarget("on_update_success_identifier", "Update");
                break;

            case "review":
                const summary = JSON.stringify(constructFinalJson(), null, 2);
                addBotMessage(`Current Configuration:\n\n${summary}`);
                setTimeout(() => showMainMenu(), 1000);
                break;

            case "save":
                handleSave();
                break;

            case "clear_rules_init":
                addBotMessage("Are you sure you want to clear all configured rules? This cannot be undone inside the assistant.", [
                    { label: "Yes, Clear All", value: "clear_rules_confirm" },
                    { label: "Cancel", value: "show_main_menu" }
                ]);
                break;
            case "clear_rules_confirm":
                setRules({
                    insert_button_label: "",
                    update_button_label: "",
                    insert_form_title: "",
                    update_form_title: "",
                    insert_success_message: "",
                    update_success_message: "",
                    generic_error_message: "",
                    on_insert_success_action: "show",
                    on_insert_success_identifier: "list",
                    on_update_success_action: "show",
                    on_update_success_identifier: "list"
                });
                addBotMessage("All rules have been cleared.");
                setTimeout(() => showMainMenu(), 600);
                break;

            default:
                // Dynamic Target Setter (set_target_insert::list)
                if (action.startsWith("set_target_")) {
                    const parts = action.split("::");
                    const context = parts[0];
                    const targetVal = parts[1];
                    const key = context === "set_target_insert" ? "on_insert_success_identifier" : "on_update_success_identifier";

                    setRules(prev => ({ ...prev, [key]: targetVal }));
                    addBotMessage(`Target set to: ${targetVal}`);
                    setTimeout(() => showMainMenu(), 600);
                } else {
                    showMainMenu();
                }
                break;
        }
    };

    const askTarget = (key, context) => {
        const prefix = context === "Insert" ? "set_target_insert" : "set_target_update";
        const options = [
            { label: "List View", value: `${prefix}::list` },
            { label: "Record Details", value: `${prefix}::record` }
        ];
        if (context === "Insert") {
            options.push({ label: "New Record Form", value: `${prefix}::new_record_form` });
        }
        addBotMessage(`Select Target View for ${context}:`, options);
    };

    const constructFinalJson = () => {
        return {
            insert_button_label: rules.insert_button_label,
            update_button_label: rules.update_button_label,
            insert_form_title: rules.insert_form_title,
            update_form_title: rules.update_form_title,
            insert_success_message: rules.insert_success_message,
            update_success_message: rules.update_success_message,
            generic_error_message: rules.generic_error_message,
            on_insert_success: {
                action: rules.on_insert_success_action,
                identifier: rules.on_insert_success_identifier
            },
            on_update_success: {
                action: rules.on_update_success_action,
                identifier: rules.on_update_success_identifier
            }
        };
    };

    const handleSave = () => {
        onSave(constructFinalJson());
        addBotMessage("Configuration saved! Closing...");
        setTimeout(onClose, 800);
    };

    if (!open) return null;

    return (
        <>
            <div
                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.3)", zIndex: 1040 }}
                onClick={onClose}
            />
            <div className="ui-rules-assistant-panel" style={{
                position: "fixed", top: 0, right: 0, bottom: 0,
                background: "#fff",
                boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
                zIndex: 1050,
                display: "flex",
                flexDirection: "column",
                animation: "slideInRight 0.3s ease-out",
                fontFamily: theme.fontFamily
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px", borderBottom: "1px solid #eaeaea", background: theme.primary, color: "#fff",
                    display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="bi bi-robot" style={{ fontSize: "1.1rem" }} />
                    </div>
                    <div style={{ flex: 1 }}><h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>UI Rules</h4></div>
                    <button onClick={onClose} style={{ border: "none", background: "none", color: "#fff", fontSize: "1.2rem", cursor: "pointer", padding: 4 }}><i className="bi bi-x-lg" /></button>
                </div>

                {/* Chat Area */}
                <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px", background: "#f4f6f9", display: "flex", flexDirection: "column", gap: 16 }}>
                    {messages.map((msg) => (
                        <div key={msg.id} style={{ alignSelf: msg.sender === 'user' ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                            <div style={{
                                padding: "10px 14px",
                                borderRadius: msg.sender === 'user' ? "12px 12px 0 12px" : "12px 12px 12px 0",
                                background: msg.sender === 'user' ? theme.primary : "#fff",
                                color: msg.sender === 'user' ? "#fff" : theme.text,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)", fontSize: "0.92rem", whiteSpace: "pre-wrap",
                                border: msg.sender === 'bot' ? "1px solid #e1e4e8" : "none"
                            }}>
                                {msg.text}
                            </div>
                            {msg.options && (
                                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {msg.options.map((opt, i) => (
                                        <button key={i} onClick={() => handleOptionSelect(opt)}
                                            style={{
                                                padding: "6px 12px", borderRadius: 16, border: `1px solid ${theme.primary}`,
                                                background: "#fff", color: theme.primary, fontSize: "0.8rem", fontWeight: 500, cursor: "pointer"
                                            }}
                                            onMouseOver={(e) => { e.currentTarget.style.background = theme.primary; e.currentTarget.style.color = "#fff"; }}
                                            onMouseOut={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = theme.primary; }}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Input Area */}
                <div style={{ padding: "16px", background: "#fff", borderTop: "1px solid #eaeaea" }}>
                    <div style={{ display: "flex", gap: 10 }}>
                        <input
                            type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                            placeholder={inputContext ? `Enter ${inputContext.label}...` : "Select an option from above..."}
                            disabled={!inputContext}
                            style={{
                                flex: 1, padding: "10px 14px", borderRadius: 20, border: "1px solid #ced4da", outline: "none",
                                fontSize: "0.95rem", background: inputContext ? "#fff" : "#f8f9fa", cursor: inputContext ? "text" : "default"
                            }}
                        />
                        <button onClick={handleTextSubmit} disabled={!inputContext || !inputValue.trim()}
                            style={{
                                width: 40, height: 40, borderRadius: "50%", background: (!inputContext || !inputValue.trim()) ? "#e9ecef" : theme.primary,
                                color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: (!inputContext || !inputValue.trim()) ? "default" : "pointer", transition: "all 0.2s"
                            }}
                        >
                            <i className="bi bi-send-fill" style={{ fontSize: "1rem" }} />
                        </button>
                    </div>
                </div>
                <style>{`
                    .ui-rules-assistant-panel {
                        width: 400px;
                    }
                    @media (max-width: 480px) {
                        .ui-rules-assistant-panel {
                            width: 100vw;
                            box-shadow: none; /* remove sidebar shadow on full width */
                        }
                    }
                    @keyframes slideInRight { 
                        from { transform: translateX(100%); opacity: 0; } 
                        to { transform: translateX(0); opacity: 1; } 
                    }
                `}</style>
            </div>
        </>
    );
}
